import { useEffect, useRef } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageBubble } from '@/components/MessageBubble';
import { StreamingMessage } from '@/components/StreamingMessage';
import { TypingIndicator } from '@/components/TypingIndicator';
import { Card, CardContent } from '@/components/ui/card';
import { MessageSquare } from 'lucide-react';
import { useFeedbackLoader } from '@/hooks/useFeedbackLoader';
import type { Message } from '@/types';

interface MessageListProps {
  messages: Message[];
  isTyping?: boolean;
  isLoading?: boolean;
  streamingMessage?: {
    content: string;
    isComplete: boolean;
    timestamp: Date;
  } | null;
  onCopy?: (content: string) => void;
  className?: string;
}

export function MessageList({
  messages,
  isTyping = false,
  isLoading = false,
  streamingMessage,
  onCopy,
  className
}: MessageListProps) {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load existing feedback for messages
  useFeedbackLoader(messages);

  // Auto-scroll to bottom when new messages arrive or streaming content updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, streamingMessage]);

  // Show empty state if no messages (only for active sessions, not welcome state)
  if (!isLoading && messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="max-w-md mx-auto border-dashed border-2 border-muted-foreground/20">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
              <MessageSquare className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2 text-muted-foreground">No messages yet</h3>
            <p className="text-muted-foreground/80 text-sm">
              Your conversation will appear here once you start chatting.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <ScrollArea className={`h-full ${className}`} ref={scrollAreaRef}>
      <div className="py-4 space-y-1 max-w-4xl mx-auto px-2 sm:px-4">
        {/* Messages */}
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            onCopy={onCopy}
          />
        ))}

        {/* Streaming message */}
        {streamingMessage && (
          <StreamingMessage
            content={streamingMessage.content}
            isComplete={streamingMessage.isComplete}
            timestamp={streamingMessage.timestamp}
          />
        )}

        {/* Typing indicator */}
        {isTyping && !streamingMessage && <TypingIndicator />}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>
    </ScrollArea>
  );
}